import { StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import EntityApiServiceRegistry from '@src/service/api/registry/entity/EntityApiServiceRegistry';
import { ICollectionData, ILemonAction, IPayloadAction, ICollectionFetchPayload } from '@src/service/business/common/types';
import { createStaticMessageUserFeedbackError } from '@src/service/business/common/userFeedbackUtils';
import { actionThunk } from '@src/service/util/observable/operators';
import { startGlobalProgress, stopGlobalProgress } from '@src/service/util/observable/operators';
import { reportCaughtMessage } from '@src/service/util/observable/operators/userFeedback';
import { IExternalContent } from '@src/model/externalcontent/ExternalContent';
import ListFilterBusinessStore from '@src/service/business/common/listFilterBusinessStore';

// -
// -------------------- Types&Consts

export interface IExternalContentListFilter {
  title?: string;
}

// List filter ID
const EXTERNAL_CONTENT_LIST_FILTER = '@@EXTERNAL_CONTENT_LIST_FILTER';

// -
// -------------------- Selectors
/** Returns external content from store. */

const getExternalContentList = (store: any): ICollectionData<IExternalContent> => store.externalContentList;
const getExternalContentListFilter = (store: any): IExternalContentListFilter => ListFilterBusinessStore.selectors.getListFilter(store, EXTERNAL_CONTENT_LIST_FILTER);

// -
// -------------------- Actions

const Actions = {
  EXTERNAL_CONTENT_LIST_FETCH: 'EXTERNAL_CONTENT_LIST_FETCH',
  EXTERNAL_CONTENT_LIST_LOAD: 'EXTERNAL_CONTENT_LIST_LOAD',
  EXTERNAL_CONTENT_LIST_CLEAR: 'EXTERNAL_CONTENT_LIST_CLEAR',
};

const fetchExternalContentList = (filter: IExternalContentListFilter, page: number, size: number, sort: string[]): IPayloadAction<ICollectionFetchPayload<IExternalContentListFilter>> => {
  return {
    type: Actions.EXTERNAL_CONTENT_LIST_FETCH,
    payload: {
      filter,
      page,
      size,
      sort,
    },
  };
};

const loadExternalContentList = (data: ICollectionData<IExternalContent>): IPayloadAction<ICollectionData<IExternalContent>> => {
  return {
    type: Actions.EXTERNAL_CONTENT_LIST_LOAD,
    payload: data,
  };
};

const clearExternalContentList = (): ILemonAction => {
  return {
    type: Actions.EXTERNAL_CONTENT_LIST_CLEAR,
  };
};

const storeExternalContentListFilter = (filter: IExternalContentListFilter): ILemonAction => {
  return ListFilterBusinessStore.actions.storeListFilter(EXTERNAL_CONTENT_LIST_FILTER, filter);
};

const clearExternalContentListFilter = (): ILemonAction => {
  return ListFilterBusinessStore.actions.clearListFilter(EXTERNAL_CONTENT_LIST_FILTER);
};

// -
// -------------------- Side-effects

const fetchExternalContentListEffect = (action$: Observable<IPayloadAction<ICollectionFetchPayload<IExternalContentListFilter>>>, state$: StateObservable<any>) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.EXTERNAL_CONTENT_LIST_FETCH;
    }),

    startGlobalProgress(),

    mergeMap((action) => {
      const payload = action.payload;

      return EntityApiServiceRegistry.getService('ExternalContent')
        .fetchEntityList(payload)
        .pipe(actionThunk(action));
    }),

    stopGlobalProgress(),

    map((data) => {
      return loadExternalContentList(data);
    }),

    reportCaughtMessage((error: any) => createStaticMessageUserFeedbackError('GENERAL_MESSAGE.GENERAL_FETCH_ERROR')),

    catchError((error: any, o: Observable<any>) => {
      console.error('Error fetching external content list', error);
      return o;
    })
  );
};

// -
// -------------------- Reducers

const externalContentList = (state: ICollectionData<IExternalContent> | null = null, action: IPayloadAction<ICollectionData<IExternalContent>>) => {
  if (action.type === Actions.EXTERNAL_CONTENT_LIST_LOAD) {
    return {
      ...action.payload,
    };
  } else if (action.type === Actions.EXTERNAL_CONTENT_LIST_CLEAR) {
    return null;
  }

  return state;
};

// --
// -------------------- Business Store

export const ExternalContentListBusinessStore = {
  actions: {
    fetchExternalContentList,
    clearExternalContentList,
    storeExternalContentListFilter,
    clearExternalContentListFilter,
  },

  selectors: {
    getExternalContentList,
    getExternalContentListFilter,
  },

  effects: {
    fetchExternalContentListEffect,
  },

  reducers: {
    externalContentList,
  },
};

// --
// export business store
export default ExternalContentListBusinessStore;
